import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
type LogoState = 'default' | 'hover' | 'pressed';
interface AnimatedLogoProps {
  state: LogoState;
  className?: string;
}
const SHAPES = [{
  id: 'bl-leaf',
  path: 'M25 78C28.866 78 32 81.134 32 85V98.5713C32 100.465 30.4648 102 28.5713 102C17.2101 102 8.00008 92.7899 8 81.4287C8 79.5352 9.53516 78 11.4287 78H25Z',
  cx: 20,
  cy: 90,
  pressX: 8.3,
  pressY: -5.5
}, {
  id: 'bc-square',
  path: 'M53 78C56.866 78 60 81.134 60 85V95C60 98.866 56.866 102 53 102H43C39.134 102 36 98.866 36 95V85C36 81.134 39.134 78 43 78H53Z',
  cx: 48,
  cy: 90,
  pressX: 4.5,
  pressY: -8.9
}, {
  id: 'br-square',
  path: 'M81 78C84.866 78 88 81.134 88 85V95C88 98.866 84.866 102 81 102H71C67.134 102 64 98.866 64 95V85C64 81.134 67.134 78 71 78H81Z',
  cx: 76,
  cy: 90,
  pressX: -4.5,
  pressY: -8.9
}, {
  id: 'mc-square',
  path: 'M53 50C56.866 50 60 53.134 60 57V67C60 70.866 56.866 74 53 74H43C39.134 74 36 70.866 36 67V57C36 53.134 39.134 50 43 50H53Z',
  cx: 48,
  cy: 62,
  pressX: 10,
  pressY: 0
}, {
  id: 'mr-square',
  path: 'M81 50C84.866 50 88 53.134 88 57V67C88 70.866 84.866 74 81 74H71C67.134 74 64 70.866 64 67V57C64 53.134 67.134 50 71 50H81Z',
  cx: 76,
  cy: 62,
  pressX: -10,
  pressY: 0
}, {
  id: 'mfr-leaf',
  path: 'M113.375 50C114.825 50 116 51.1753 116 52.625C116 64.4301 106.43 74 94.625 74C93.1753 74 92 72.8247 92 71.375V57C92 53.134 95.134 50 99 50H113.375Z',
  cx: 104,
  cy: 62,
  pressX: -10,
  pressY: 0
}, {
  id: 'tc-square',
  path: 'M53 22C56.866 22 60 25.134 60 29V39C60 42.866 56.866 46 53 46H43C39.134 46 36 42.866 36 39V29C36 25.134 39.134 22 43 22H53Z',
  cx: 48,
  cy: 34,
  pressX: 4.5,
  pressY: 8.9
}, {
  id: 'tr-square',
  path: 'M81 22C84.866 22 88 25.134 88 29V39C88 42.866 84.866 46 81 46H71C67.134 46 64 42.866 64 39V29C64 25.134 67.134 22 71 22H81Z',
  cx: 76,
  cy: 34,
  pressX: -4.5,
  pressY: 8.9
}, {
  id: 'tfr-leaf',
  path: 'M98.2227 22C108.041 22.0002 116 29.9593 116 39.7773C116 43.2138 113.214 46 109.777 46H99C95.134 46 92 42.866 92 39V28.2227C92 24.7862 94.7862 22 98.2227 22Z',
  cx: 104,
  cy: 34,
  pressX: -8.3,
  pressY: 5.5
}];
const MAX_DISTANCE = 81;
const REPEL_STRENGTH = 13;
const MAX_DISPLACEMENT = 4;
const FALLOFF_EXPONENT = 1.7;
function Shape({
  shape,
  mouseX,
  mouseY,
  state
}: any) {
  const targetX = useTransform([mouseX, mouseY], ([mx, my]: number[]) => {
    if (state === 'pressed') return shape.pressX;
    if (state === 'default' || mx === -1000) return 0;
    const dx = shape.cx - mx;
    const dy = shape.cy - my;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < MAX_DISTANCE && dist > 0) {
      const force = Math.pow((MAX_DISTANCE - dist) / MAX_DISTANCE, FALLOFF_EXPONENT);
      const displacementX = dx / dist * force * REPEL_STRENGTH;
      return Math.max(-MAX_DISPLACEMENT, Math.min(MAX_DISPLACEMENT, displacementX));
    }
    return 0;
  });
  const targetY = useTransform([mouseX, mouseY], ([mx, my]: number[]) => {
    if (state === 'pressed') return shape.pressY;
    if (state === 'default' || my === -1000) return 0;
    const dx = shape.cx - mx;
    const dy = shape.cy - my;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < MAX_DISTANCE && dist > 0) {
      const force = Math.pow((MAX_DISTANCE - dist) / MAX_DISTANCE, FALLOFF_EXPONENT);
      const displacementY = dy / dist * force * REPEL_STRENGTH;
      return Math.max(-MAX_DISPLACEMENT, Math.min(MAX_DISPLACEMENT, displacementY));
    }
    return 0;
  });
  const springX = useSpring(targetX, {
    stiffness: 180,
    damping: 14,
    mass: 1
  });
  const springY = useSpring(targetY, {
    stiffness: 180,
    damping: 14,
    mass: 1
  });
  return <motion.path d={shape.path} fill="black" style={{
    x: springX,
    y: springY
  }} data-id="element-12" />;
}
export function AnimatedLogo({
  state,
  className = ''
}: AnimatedLogoProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const mouseX = useMotionValue(-1000);
  const mouseY = useMotionValue(-1000);
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!svgRef.current || state === 'default') return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width * 124;
    const y = (e.clientY - rect.top) / rect.height * 124;
    mouseX.set(x);
    mouseY.set(y);
  };
  const handlePointerLeave = () => {
    mouseX.set(-1000);
    mouseY.set(-1000);
  };
  return <motion.svg ref={svgRef} viewBox="0 0 124 124" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} onPointerMove={handlePointerMove} onPointerLeave={handlePointerLeave} animate={{
    scale: state === 'pressed' ? 0.95 : 1
  }} transition={{
    duration: 0.12
  }} data-id="element-13">
      {SHAPES.map(shape => <Shape key={shape.id} shape={shape} mouseX={mouseX} mouseY={mouseY} state={state} data-id="element-14" />)}
    </motion.svg>;
}